/* eslint-disable eqeqeq */
import { Icon } from 'semantic-ui-react';
import { CustomTooltip } from '../../../components';
import {
  findIndex,
  flattenDeep,
  get,
  isEmpty,
  sortBy,
  sum,
} from '../../../utils/lodash';
import {
  CompletionProgress,
  CompletionProgressGrant,
} from '../../../utils/constant';
import { getActivityType } from '../../../utils';
import { isParentProgramActivity } from '../../../utils/activity';
import { hasReportableExpenditure } from '../../../utils/activity/financials';
import { useTranslation } from 'react-i18next';

const hasText = (value) => Boolean(value && String(value).trim() !== '');

const parseInclusions = (raw) => {
  try {
    return JSON.parse(raw || '{}');
  } catch {
    return {};
  }
};

// TAK-2086: total expenses (Venue Hire, contractors, Other expenditure), or
// 'No reportable expenditure' ticked
const isActivityPlanExpenditureComplete = (currentActivity) =>
  hasReportableExpenditure({
    activity: currentActivity,
    actual: currentActivity.statusId >= 2,
  });

const isEvaluationPlanComplete = (plan) => {
  if (!plan || plan.isDisabled) return true;
  const selfAssessed = plan.methodId === 9;
  if (!selfAssessed) return Boolean(plan.dateMeasured);

  const questions = get(plan, 'evaluationQuestions', []);
  const questionsComplete =
    !isEmpty(questions) &&
    questions.every((question) => {
      const hasReason = Boolean(get(question, 'selfAssessmentReason', null));
      const hasResponses =
        sum(flattenDeep(get(question, 'participantResponse', []))) !== 0;
      return hasReason && hasResponses;
    });

  return Boolean(
    get(plan, 'methodReason', null) &&
    !isEmpty(get(plan, 'assessmentTypes', [])) &&
    get(plan, 'actualParticipantsEvaluated', null) &&
    questionsComplete
  );
};

const isEvaluationComplete = (currentActivity) => {
  const plans = currentActivity.evaluationPlans || [];
  if (isEmpty(plans)) return false;
  if (!plans.every(isEvaluationPlanComplete)) return false;

  const inclusions = parseInclusions(currentActivity.reportInclusions);
  const methodologyIncluded = inclusions.methodology !== false;
  return !methodologyIncluded || hasText(currentActivity.evaluationMethodology);
};

const isDocumentationComplete = (currentActivity) => {
  if (!hasText(currentActivity.reflection)) return false;
  if (!hasText(currentActivity.result)) return false;

  const inclusions = parseInclusions(currentActivity.reportInclusions);

  if (inclusions.execSummary !== false && !hasText(currentActivity.summary)) {
    return false;
  }

  const normalEvaluation = (currentActivity.evaluationPlans || []).filter(
    (plan) => plan.methodId !== 9
  );
  const showAnalysis =
    currentActivity.isAnalysisField &&
    (isParentProgramActivity(currentActivity.activityPlanTypeId) ||
      !isEmpty(normalEvaluation));
  if (
    showAnalysis &&
    inclusions.findings !== false &&
    !hasText(currentActivity.analysis)
  ) {
    return false;
  }

  if (inclusions.nextSteps !== false && !hasText(currentActivity.nextSteps)) {
    return false;
  }

  return true;
};

const CustomItem = (props) => {
  const {
    title,
    statusList,
    statusId,
    activityPlanTypeId,
    currentActivity,
    popupState,
    popupContent,
    popupContentObject,
    popupPosition,
    smallText,
    activity_step_id,
  } = props;

  const { t } = useTranslation();

  const isDevelopmentActivity = statusId === 1;
  const isActiveActivity = statusId === 2;

  const RenderIcon = () => {
    let status = 0;
    const actType = getActivityType({ activityPlanTypeId });

    let _completionProgress = [];
    const _statusList = sortBy(statusList, 'section').filter(
      (status) => status.section.toUpperCase() !== 'GREEN LIGHT'
    );

    switch (actType.type) {
      case 'Single':
        _completionProgress = sortBy(CompletionProgress, 'section');
        const singleIndex = findIndex(
          _completionProgress,
          (o) => o.section.toLowerCase() === activity_step_id.toLowerCase()
        );
        status = _statusList[singleIndex].status || 0;
        break;
      default:
        _completionProgress = sortBy(CompletionProgressGrant, 'section');

        const grantIndex = findIndex(
          _completionProgress,
          (o) => o.section.toLowerCase() === activity_step_id.toLowerCase()
        );

        status = _statusList[grantIndex].status || 0;
        break;
    }

    const goalsData = statusList.find((item) => item.section === 'Goal');

    /*
     * IF NO GOAL SET YET
     */
    if (goalsData.status < 2 && activity_step_id !== 'Goal') {
      return null;
    }

    /*
     * check whether all the green light rules are met
     */

    if (statusId === 3) {
      return (
        <Icon circular inverted className="blue" name="check" size="small" />
      );
    }

    let iconProp = {
      className: '',
      name: '',
    };

    /*
     * *********************** Not started yet and unable to edit (some requirement not met yet in previous tabs or need validations to completed) ***********************
     */
    if (status === 0) {
      if (activity_step_id === 'Goal') {
        // if goal are set need to show check icon
        iconProp = {
          name: currentActivity.goal ? 'check' : '',
          className: 'whitebox',
        };
      } else if (
        activity_step_id === 'Objective' &&
        actType.type === 'Single'
      ) {
        // iconProp = { ...iconProp, className: '' };
        return null;
      } else if (activity_step_id === 'Evidence') {
        // iconProp = { ...iconProp, className: 'whitebox' };
        return null;
      } else if (activity_step_id === 'Theory of Change') {
        // iconProp = { ...iconProp, className: 'whitebox' };
        return null;
      } else if (activity_step_id === 'Activity Plan') {
        // iconProp = { ...iconProp, className: 'whitebox' };
        return null;
      } else if (activity_step_id === 'Evaluation') {
        // iconProp = { ...iconProp, className: 'whitebox' };
        return null;
      } else if (activity_step_id === 'Documentation') {
        // iconProp = { ...iconProp, className: 'whitebox' };
        return null;
      } else if (activity_step_id === 'Report') {
        if (statusId === 1) {
          // iconProp = { ...iconProp, className: 'whitebox' };
          return null;
        }
        // in active
        if (statusId === 2) {
          iconProp = { name: '', className: 'green' };
        }
      } else return null;

      /*
       *********************** all the required fields are met  status ***********************
       */
    } else if (status === 1) {
      if (activity_step_id === 'Goal') {
        iconProp = {
          ...iconProp,
          className: 'whitebox',
        };
      } else if (activity_step_id === 'Objective') {
        iconProp = {
          ...iconProp,
          className: 'whitebox',
        };
      } else if (activity_step_id === 'Evidence') {
        iconProp = {
          ...iconProp,
          className: 'whitebox',
        };
      } else if (activity_step_id === 'Theory of Change') {
        iconProp = {
          ...iconProp,
          className: 'whitebox',
        };
      } else if (activity_step_id === 'Activity Plan') {
        iconProp = {
          ...iconProp,
          className: isActiveActivity ? 'green' : 'whitebox',
        };
      } else if (activity_step_id === 'Evaluation') {
        iconProp = {
          ...iconProp,
          className: isDevelopmentActivity
            ? 'whitebox'
            : isActiveActivity
              ? 'green'
              : 'blue',
        };
      } else if (activity_step_id === 'Documentation') {
        // if()
        iconProp = {
          // name: isArchivedValid ? 'check' : '',
          ...iconProp,
          className: 'green',
        };
      } else if (activity_step_id === 'Report') {
        iconProp = {
          // name: isArchivedValid ? 'check' : '',
          ...iconProp,
          className: isDevelopmentActivity ? 'whitebox' : 'green',
        };
      } else return null;

      /*
       * *********************** completed  status [NOTE: When all requirements met ***********************
       */
    } else if (status === 2) {
      if (activity_step_id === 'Goal') {
        iconProp = {
          name: 'check',
          className: 'blue',
        };
      } else if (activity_step_id === 'Objective') {
        iconProp = {
          name: 'check',
          className:
            isDevelopmentActivity &&
            !get(currentActivity, 'isLinkedActivity', false)
              ? 'whitebox'
              : 'blue',
        };
      } else if (activity_step_id === 'Evidence') {
        iconProp = {
          name: 'check',
          className: isDevelopmentActivity ? 'whitebox' : 'blue',
        };
      } else if (activity_step_id === 'Theory of Change') {
        iconProp = {
          name: 'check',
          className: isDevelopmentActivity ? 'whitebox' : 'blue',
        };
      } else if (activity_step_id === 'Activity Plan') {
        iconProp = {
          name: isActivityPlanExpenditureComplete(currentActivity)
            ? 'check'
            : '',
          className: isDevelopmentActivity
            ? 'whitebox'
            : isActiveActivity
              ? 'green'
              : 'blue',
        };
      } else if (activity_step_id === 'Evaluation') {
        iconProp = {
          name: isActiveActivity
            ? isEvaluationComplete(currentActivity)
              ? 'check'
              : ''
            : !isEmpty(currentActivity.evaluationPlans)
              ? 'check'
              : '',
          className: isDevelopmentActivity
            ? 'whitebox'
            : isActiveActivity
              ? 'green'
              : 'blue',
        };
      } else if (activity_step_id === 'Documentation') {
        iconProp = {
          name: isActiveActivity
            ? isDocumentationComplete(currentActivity)
              ? 'check'
              : ''
            : 'check',
          className: isDevelopmentActivity
            ? 'whitebox'
            : isActiveActivity
              ? 'green'
              : 'blue',
        };
      } else if (activity_step_id === 'Report') {
        if (isDevelopmentActivity) {
          iconProp = { name: 'check', className: 'whitebox' };
        }
        if (isActiveActivity) {
          iconProp = {
            name: 'check',
            className: 'green',
          };
        }
      } else return null;
    } else {
    }

    return (
      <Icon
        circular
        inverted
        className={iconProp.className}
        name={iconProp.name}
        size="small"
      />
    );
  };

  return (
    <CustomTooltip
      state={popupState}
      content={popupContent}
      contentObject={popupContentObject}
      position={popupPosition}
    >
      <div className="customSteps">
        <RenderIcon />
        <label className={`customStep-label ${smallText ? 'small-text' : ''}`}>
          {title}
        </label>
      </div>
    </CustomTooltip>
  );
};

export { CustomItem };
